import * as React from 'react';
import { useLocation } from 'react-router-dom';

import {
	FieldProps, Form, Formik, FormikProps, FormikHelpers,
} from 'formik';
import * as Yup from 'yup';

import Rate from 'antd/lib/rate';
import Message from 'antd/lib/message';

import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import Button from '@common/react/components/Forms/Button';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import dummyAvatar from '@common/react/images/avatar.jpg';

import { LogoLoaderPage } from '@commonTuna/react/components/UI/LogoLoader/LogoLoader';

import { DoctorPage } from '@app/objects/Pages/DoctorPage';

import '@app/scss/pages/doctorReview.scss';
import { request } from '@app/components/Api';
import { Review } from '@app/objects/Review';
import { DoctorReview } from '@app/objects/DoctorReview';
import DoctorAvatar from '@app/components/UI/DoctorCard/DoctorAvatar';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';

interface DoctorPortalProps {
	pathname: string;
	serverPage: DoctorPage;
}

const options: Intl.DateTimeFormatOptions = {
	year: 'numeric',
	month: 'numeric',
	day: 'numeric',
	hour: 'numeric',
	minute: 'numeric',
};

type FormValues = Partial<Review>;

const validationSchema = Yup.object().shape({
	textEn: simpleStringValidator,
});

const DoctorPortalComponent: React.FC<DoctorPortalProps> = (props) => {
	const { serverPage } = props;
	const content = serverPage?.item;
	const [saveLoading, setSaveLoading] = React.useState<boolean>(false);

	const location = useLocation();
	const params = getSearchParamsFromUrl(location) as any;

	const [appointmentId] = React.useState<number | null>(params.appointmentId);

	const scheduleTime = content?.appointment?.scheduledTime
		? new Intl.DateTimeFormat('en-US', options).format(content?.appointment?.scheduledTime)
		: '-';

	const rateFontSize = React.useMemo(() => {
		return 30 - content.doctor.totalReviewCount.toString().length * 2;
	}, [content.doctor.totalReviewCount]);

	const onSubmit = (formValues: FormValues, formikActions: FormikHelpers<FormValues>) => {
		setSaveLoading(true);

		request<Review>('review', {
			...formValues,
			appointmentId,
		})
			.then((res) => {
				formikActions.resetForm();
				Message.success('Your review was successfully sent!');
			})
			.catch((err) => {
				Message.error(err);
			})
			.finally(() => setSaveLoading(false));
	};

	const initialValues: Partial<Review> = {
		id: -1,
		textEn: '',
		textEs: '',
		rate: 5,
		doctors: content?.doctor.id > 0 ? [
			{
				id: -1,
				doctorId: content?.doctor.id,
			} as DoctorReview]
			: [],
	};

	if (!content) {
		return <LogoLoaderPage />;
	}

	return <>
		<div className="doctor-portal">
			<div className="doctor-review-content">
				<div className="white-block">
					<Formik
						initialValues={initialValues}
						onSubmit={(formik, actions) => onSubmit(formik, actions)}
						enableReinitialize
						validationSchema={validationSchema}
					>
						{(formikBag: FormikProps<FormValues>) => {
							return <Form id="review-form">
								<div className="main-content-block clearfix">
									<div>
										<div className="avatar-block left-pull">
											<div className="avatar">
												<DoctorAvatar
													hideWhenLoading
													withLink={false}
													alt={content?.doctor.nameEn}
													src={content?.doctor.avatar ? content?.doctor.avatar : dummyAvatar}
												/>
											</div>
											<div className="doctor-rate mt10" style={{ fontSize: `${rateFontSize}px` }}>
												<strong className="mr10">
													{Array.from({
														length: Math.round(content.doctor.averageReviewRate || 0),
													})
														.map((_, i) => i)
														.map((i) => (
															<i
																className="fa fa-star"
																key={i}
																aria-hidden="true"
															/>
														))}
												</strong>
												(
												{content.doctor.totalReviewCount}
												)
											</div>
											{content.doctor.specialties?.length > 0
												&& <div className="mt10">
													<h6>
														{
															content.doctor.specialties.map((s) => s.specialty && (
																<SpecialtyTag
																	key={s.id}
																	specialty={s.specialty}
																	tagClassName="specialty"
																/>
															))}
													</h6>
												</div>
											}
										</div>
										<div className="right-pull main-info">
											<div>
												<div>
													<h1 style={{ fontSize: '33px' }}>
														Leave your feedback about
														{content.doctor.nameEn}
													</h1>
													<h3>
														You had an appointment with this doctor at&nbsp;
														<strong>{scheduleTime}</strong>
														. So, how did it go with
														<strong>{content.doctor.nameEn}</strong>
														?
													</h3>
													<div className="orange-block">
														<div className="row">
															<FormikField
																fieldName="rate"
																title=""
																render={({ field, form }) => (
																	<Rate
																		value={field.value}
																		onChange={(value) => {
																			form.setFieldValue(field.name, +value);
																		}}
																	/>
																)}
															/>
														</div>
														<div className="row">
															<FormikField
																fieldName="textEn"
																title=""
																containerClassName="form-group col-sm-12"
																render={({ form, field }: FieldProps) =>
																	<textarea
																		{...field}
																		className="form-control"
																		id={field.name}
																		value={field.value}
																		style={{ minHeight: 150, resize: 'none' }}
																	/>}
															/>
														</div>
														<div className="text-center">
															<Button
																type="button"
																className="btn btn-primary"
																onClick={() => formikBag.submitForm()}
																isLoading={saveLoading}
															>
																Leave Review
															</Button>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</Form>;
						}}
					</Formik>
				</div>
			</div>
		</div>
	</>;
};

const DoctorPortalComponentWithServer: React.FC = (props) => {
	return (<ServerPageProvider
		path="doctor"
		loader={<LogoLoaderPage />}
		inner={(serverPage) =>
			<DoctorPortalComponent
				{...props as any}
				serverPage={serverPage}
			/>
		}
	/>);
};

export default DoctorPortalComponentWithServer;
